import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SignalRService, SignalREventHandler } from './signalr.service';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { AlertService } from './alert.service';
import { ModalService } from '../components/modal/modal.service';
import { MODULE_NAV_CONFIG } from '../models/permission';

/** Primary event name from backend contract */
const PERMISSION_UPDATED_EVENT = 'OnPermissionUpdated';
const USER_DEACTIVATED_EVENT = 'OnUserDeactivated';

/**
 * Alternate names to detect event-name mismatch.
 * If any of these fire, console will show which name the server actually uses.
 */
const DEBUG_ALT_EVENTS = [
  'OnPermissionUpdated',
  'PermissionUpdated',
  'ReceivePermissionUpdate',
  'ReceiveNotification',
  'PermissionChanged',
  'NotifyPermissionUpdated'
];

const DEBOUNCE_MS = 2000;

interface PermissionUpdatedPayload {
  title: string;
  message: string;
  targetUserId: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationHubService {
  private signalRService = inject(SignalRService);
  private authService = inject(AuthService);
  private permissionService = inject(PermissionService);
  private alertService = inject(AlertService);
  private modalService = inject(ModalService);
  private router = inject(Router);

  private initialized = false;
  private lastPermissionUpdateAt = 0;

  private readonly permissionUpdatedHandler: SignalREventHandler = (...args: unknown[]) => {
    console.log('[NotificationHub] >>> EVENT RECEIVED:', PERMISSION_UPDATED_EVENT, {
      argCount: args.length,
      args,
      argTypes: args.map(a => typeof a)
    });
    const payload = this.parsePermissionUpdatedArgs(args);
    console.log('[NotificationHub] Parsed payload', payload);
    this.handlePermissionUpdated(payload);
  };

  private readonly userDeactivatedHandler: SignalREventHandler = (...args: unknown[]) => {
    console.log('[NotificationHub] >>> EVENT RECEIVED:', USER_DEACTIVATED_EVENT, {
      argCount: args.length,
      args,
      argTypes: args.map(a => typeof a)
    });

    const payload = this.parsePermissionUpdatedArgs(args);
    const currentUserId = this.authService.getCurrentUser()?.userId;

    if (!currentUserId) {
      return;
    }

    if (payload.targetUserId && this.isSameUserId(payload.targetUserId, currentUserId)) {
      console.log('[NotificationHub] User deactivated matching current user. Logging out in 2 seconds...');

      const title = payload.title || 'Account Deactivated';
      const message = payload.message || 'Your account has been deactivated by an administrator.';

      // Show a warning toaster — it will auto-dismiss after 2 seconds
      this.alertService.warning(message, title, 2000);

      // After 2 seconds, call the logout API and redirect to the login page
      setTimeout(() => {
        this.authService.logout().subscribe({
          next: () => this.router.navigate(['/login']),
          error: () => this.router.navigate(['/login'])
        });
      }, 0);
    }
  };

  /** Logs any alternate event so we can spot a wrong method name */
  private readonly debugAltHandler = (eventName: string): SignalREventHandler => (...args: unknown[]) => {
    console.warn(`[NotificationHub] ALTERNATE EVENT FIRED: "${eventName}" — primary listener is "${PERMISSION_UPDATED_EVENT}"`, {
      argCount: args.length,
      args
    });
  };

  private readonly altHandlers = new Map<string, SignalREventHandler>();

  initialize(): void {
    console.log('[NotificationHub] initialize() called', {
      alreadyInitialized: this.initialized,
      currentUser: this.authService.getCurrentUser()
    });

    if (this.initialized) {
      console.log('[NotificationHub] Already initialized — skipping');
      return;
    }

    this.signalRService.on(PERMISSION_UPDATED_EVENT, this.permissionUpdatedHandler);
    this.signalRService.on(USER_DEACTIVATED_EVENT, this.userDeactivatedHandler);

    // Register alternate listeners once (skip duplicate of primary)
    for (const eventName of DEBUG_ALT_EVENTS) {
      if (eventName === PERMISSION_UPDATED_EVENT) {
        continue;
      }
      const handler = this.debugAltHandler(eventName);
      this.altHandlers.set(eventName, handler);
      this.signalRService.on(eventName, handler);
    }

    console.log('[NotificationHub] Starting SignalR connection...');
    this.signalRService.start()
      .then(() => {
        console.log('[NotificationHub] Hub start completed', {
          isConnected: this.signalRService.isConnected,
          connectionId: this.signalRService.connectionId,
          state: this.signalRService.connectionState,
          listeningFor: PERMISSION_UPDATED_EVENT,
          alsoWatching: DEBUG_ALT_EVENTS.filter(e => e !== PERMISSION_UPDATED_EVENT)
        });
      })
      .catch((error) => {
        console.error('[NotificationHub] Hub connection FAILED', error);
      });

    this.initialized = true;
  }

  shutdown(): void {
    console.log('[NotificationHub] shutdown() called', { wasInitialized: this.initialized });

    if (!this.initialized) {
      return;
    }

    this.signalRService.off(PERMISSION_UPDATED_EVENT, this.permissionUpdatedHandler);
    this.signalRService.off(USER_DEACTIVATED_EVENT, this.userDeactivatedHandler);
    this.altHandlers.forEach((handler, eventName) => {
      this.signalRService.off(eventName, handler);
    });
    this.altHandlers.clear();

    this.signalRService.stop().catch((error) => {
      console.error('[NotificationHub] Disconnect failed:', error);
    });
    this.signalRService.reset();

    this.initialized = false;
    this.lastPermissionUpdateAt = 0;
  }

  private parsePermissionUpdatedArgs(args: unknown[]): PermissionUpdatedPayload {
    if (args.length === 1 && args[0] && typeof args[0] === 'object' && !Array.isArray(args[0])) {
      const payload = args[0] as Record<string, unknown>;
      return {
        title: String(payload['title'] ?? payload['Title'] ?? ''),
        message: String(payload['message'] ?? payload['Message'] ?? ''),
        targetUserId: this.normalizeUserId(payload['userId'] ?? payload['UserId'] ?? payload['targetUserId'])
      };
    }

    const title = String(args[0] ?? '');
    const message = String(args[1] ?? '');
    const thirdArg = args[2];

    if (typeof thirdArg === 'string' || typeof thirdArg === 'number') {
      return { title, message, targetUserId: this.normalizeUserId(thirdArg) };
    }

    if (thirdArg && typeof thirdArg === 'object' && !Array.isArray(thirdArg)) {
      const payload = thirdArg as Record<string, unknown>;
      return {
        title,
        message,
        targetUserId: this.normalizeUserId(payload['userId'] ?? payload['UserId'])
      };
    }

    return { title, message, targetUserId: null };
  }

  private normalizeUserId(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).trim();
    return normalized || null;
  }

  private isSameUserId(left: string | null | undefined, right: string | null | undefined): boolean {
    if (!left || !right) {
      return false;
    }

    return left.trim().toLowerCase() === right.trim().toLowerCase();
  }

  private handlePermissionUpdated(payload: PermissionUpdatedPayload): void {
    const currentUserId = this.authService.getCurrentUser()?.userId;
    const { title, message, targetUserId } = payload;

    console.log('[NotificationHub] handlePermissionUpdated', {
      currentUserId,
      targetUserId,
      title,
      message,
      willSkipNoUser: !currentUserId,
      willSkipMismatch: !!(targetUserId && currentUserId && !this.isSameUserId(targetUserId, currentUserId))
    });

    if (!currentUserId) {
      console.warn('[NotificationHub] SKIP — no current user');
      return;
    }

    if (targetUserId && !this.isSameUserId(targetUserId, currentUserId)) {
      console.warn('[NotificationHub] SKIP — userId mismatch', { currentUserId, targetUserId });
      return;
    }

    const now = Date.now();
    if (now - this.lastPermissionUpdateAt < DEBOUNCE_MS) {
      console.warn('[NotificationHub] SKIP — debounced');
      return;
    }
    this.lastPermissionUpdateAt = now;

    const user = this.authService.getCurrentUser();
    console.log('[NotificationHub] Refreshing permissions...', { userId: user?.userId, roleId: user?.roleId });

    this.permissionService.refreshPermissionsForUser(user?.userId, user?.roleId).subscribe({
      next: (response) => {
        console.log('[NotificationHub] Permissions refresh response', response);

        if (!response) {
          console.warn('[NotificationHub] Empty permission response — still showing alerts');
        }

        const alertTitle = title?.trim() || 'Permissions Updated';
        const alertMessage = message?.trim() || 'Your module permissions have been updated by an administrator.';

        console.log('[NotificationHub] Showing toaster + modal', { alertTitle, alertMessage });
        this.alertService.info(alertMessage, alertTitle);
        this.modalService.alert(alertTitle, alertMessage, 'info');
        this.redirectIfUnauthorized();
      },
      error: (error) => {
        console.error('[NotificationHub] Failed to refresh permissions after notification:', error);
        this.alertService.error(
          'Failed to refresh permissions. Please try logging in again.',
          'Error'
        );
      }
    });
  }

  private redirectIfUnauthorized(): void {
    const user = this.authService.getCurrentUser();
    if (!this.permissionService.shouldFetchPermissions(user?.roleId)) {
      return;
    }

    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    const publicRoutes = ['/profile'];

    if (publicRoutes.some(route => currentUrl === route || currentUrl.startsWith(`${route}/`))) {
      return;
    }

    if (!this.permissionService.canView(currentUrl)) {
      const firstAllowedRoute = this.findFirstAllowedRoute();
      console.log('[NotificationHub] Redirecting — route no longer allowed', {
        currentUrl,
        firstAllowedRoute
      });
      this.router.navigate([firstAllowedRoute || '/profile']);
    }
  }

  private findFirstAllowedRoute(): string | null {
    const moduleRoutes = Object.values(MODULE_NAV_CONFIG).map(config => config.route);

    for (const route of moduleRoutes) {
      if (this.permissionService.canView(route)) {
        return route;
      }
    }

    return null;
  }
}
